/**
 * App Catalogue — the code pool for ONE app.
 *
 * Route: /admin/app-catalogue/codes/:appId
 *
 * The panel itself is shared with the Distribution detail page (AppCodePool), so there is
 * exactly one implementation of "upload codes, assign them, see which were used".
 */

import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IconArrowLeft, IconRefresh, IconDeviceMobile } from '@tabler/icons-react';
import {
  Stack,
  Group,
  Button,
  Text,
  Title,
  Alert,
  Avatar,
  Loader,
} from '@/components/ui';
import { FadeIn } from '@/components/animations';
import { useApplicationCatalogueService, useAppDistributionService } from '@actschurch/ccms-sdk';
import { ADMIN_ROUTES } from '@/config/routes';
import AppCodePool from './components/AppCodePool';

export default function CodesDetailPage() {
  const { appId = '' } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const catalogueService = useApplicationCatalogueService();
  const distributionService = useAppDistributionService();

  const distributionsKey = ['admin', 'app-catalogue', 'distributions', appId];

  const { data: apps = [], isLoading: appsLoading } = useQuery({
    queryKey: ['admin', 'app-catalogue', 'all'],
    queryFn: () => catalogueService.getAllApps(),
  });

  const {
    data: distributions = [],
    isLoading: distributionsLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: distributionsKey,
    queryFn: () => distributionService.getDistributions(appId),
    enabled: Boolean(appId),
  });

  const app = apps.find((a) => a.appId === appId) ?? null;
  const pool = distributions.find((d) => d.yieldsCode);

  const refresh = () => queryClient.invalidateQueries({ queryKey: distributionsKey });
  const back = () => navigate(ADMIN_ROUTES.APP_CATALOGUE_CODES);

  if (appsLoading || distributionsLoading) {
    return (
      <Stack gap="lg">
        <Title order={2}>Codes</Title>
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack gap="lg">
        <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />} onClick={back}>
          All pools
        </Button>
        <Alert color="red" title="Can't reach the distribution service">
          <Text size="sm">
            {error instanceof Error ? error.message : 'The Application Catalogue did not respond.'}
          </Text>
          <Button variant="light" color="red" size="xs" mt="sm" onClick={refresh}>
            Retry
          </Button>
        </Alert>
      </Stack>
    );
  }

  if (!app) {
    return (
      <Stack gap="lg">
        <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />} onClick={back}>
          All pools
        </Button>
        <Alert color="yellow" title="App not found">
          <Text size="sm">No app in the catalogue has the id "{appId}".</Text>
        </Alert>
      </Stack>
    );
  }

  return (
    <FadeIn>
      <Stack gap="lg">
        <Button
          variant="subtle"
          size="xs"
          leftSection={<IconArrowLeft size={14} />}
          onClick={back}
          style={{ alignSelf: 'flex-start' }}
        >
          All pools
        </Button>

        <Group justify="space-between" align="flex-start">
          <Group gap="md">
            <Avatar
              src={app.iconUrl || undefined}
              radius="md"
              size="lg"
              style={{ backgroundColor: app.colors?.primary || undefined }}
            >
              {app.shortName?.charAt(0) ?? '?'}
            </Avatar>
            <div>
              <Title order={2}>{app.displayName}</Title>
              <Text c="dimmed" size="sm">
                Redemption codes {pool ? `— ${pool.platformName}` : ''}
              </Text>
            </div>
          </Group>
          <Button
            variant="subtle"
            size="sm"
            leftSection={<IconRefresh size={16} />}
            onClick={refresh}
            loading={isFetching}
          >
            Refresh
          </Button>
        </Group>

        {pool ? (
          <AppCodePool
            distribution={pool}
            targetLabel={`${app.displayName} — ${pool.platformName}`}
            onChanged={refresh}
          />
        ) : (
          <Alert color="yellow" title="This app doesn't use codes">
            <Text size="sm">
              Its distribution method doesn't hand out redemption codes. Set the iOS method to
              <strong> Apple Redemption Code</strong> if it should.
            </Text>
            <Button
              size="xs"
              variant="light"
              color="yellow"
              mt="sm"
              leftSection={<IconDeviceMobile size={14} />}
              onClick={() => navigate(`/admin/app-catalogue/distribution/${app.appId}`)}
            >
              Open distribution settings
            </Button>
          </Alert>
        )}
      </Stack>
    </FadeIn>
  );
}
